import {
  BookOpen,
  Briefcase,
  CalendarDays,
  Clock,
  GraduationCap,
  HandHeart,
  HeartPulse,
  MapPin,
  Megaphone,
  Music,
  ShieldCheck,
  Sparkles,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import { useSiteSettings } from "@/lib/use-site-settings";

const ICONS: Record<string, LucideIcon> = {
  BookOpen,
  Briefcase,
  CalendarDays,
  Clock,
  GraduationCap,
  HandHeart,
  HeartPulse,
  MapPin,
  Megaphone,
  Music,
  ShieldCheck,
  Sparkles,
  Users,
};

export function DynamicIcon({ name, className }: { name?: string | null; className?: string }) {
  const Icon = (name && ICONS[name]) || Sparkles;
  return <Icon className={className} aria-hidden="true" />;
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
      {children}
    </span>
  );
}

export function Section({
  children,
  className,
  id,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section id={id} className={cn("px-4 py-16 md:py-24", className)}>
      <div className="mx-auto w-full max-w-6xl">{children}</div>
    </section>
  );
}

export function SectionTitle({
  eyebrow,
  title,
  description,
  align = "left",
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  align?: "left" | "center";
}) {
  return (
    <div className={cn("max-w-3xl", align === "center" && "mx-auto text-center")}>
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <h2 className="mt-4 text-3xl md:text-5xl text-balance-title">{title}</h2>
      {description ? (
        <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
          {description}
        </p>
      ) : null}
    </div>
  );
}

export function PageHero({
  eyebrow,
  title,
  description,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
}) {
  const { pageHeroBgUrl } = useSiteSettings();
  return (
    <header className="relative overflow-hidden bg-hero-gradient px-4 pt-28 pb-16 text-primary-foreground md:pt-36 md:pb-24">
      {pageHeroBgUrl ? (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${pageHeroBgUrl})` }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-hero-gradient opacity-80 mix-blend-multiply" aria-hidden="true" />
        </>
      ) : null}
      <div className="absolute inset-0 bg-mesh opacity-40" aria-hidden="true" />
      <div className="relative mx-auto w-full max-w-6xl">
        {eyebrow ? (
          <span className="inline-flex rounded-full bg-accent px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-accent-foreground">
            {eyebrow}
          </span>
        ) : null}
        <h1 className="mt-5 text-4xl md:text-6xl text-balance-title">{title}</h1>
        {description ? (
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-primary-foreground/85 md:text-lg">
            {description}
          </p>
        ) : null}
      </div>
    </header>
  );
}

export function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center text-sm text-muted-foreground">
      {message}
    </div>
  );
}

export function RichText({ html }: { html?: string | null }) {
  if (!html) return null;
  return <div className="prose-campaign" dangerouslySetInnerHTML={{ __html: html }} />;
}

export function ShareButtons({ title }: { title: string }) {
  const url = typeof window === "undefined" ? "" : window.location.href;
  const links = [
    { label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}` },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
    { label: "X", href: `https://x.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}` },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Compartilhe
      </span>
      {links.map((l) => (
        <a
          key={l.label}
          href={l.href}
          target="_blank"
          rel="noreferrer"
          className="rounded-full border border-border px-3 py-1 text-sm font-medium text-foreground transition-colors hover:border-primary hover:text-primary"
        >
          {l.label}
        </a>
      ))}
    </div>
  );
}
